/**
 * Processes Tab Prompt Builder
 *
 * Generates swimlane flow diagrams from organization and relationship data.
 * Shows business process steps across organizational lanes.
 */

import type { BulkEAImport } from '../../canister-client';
import type { ChatMessage } from '../../canister-client';
import { baseSvgRules, summarizeEntities } from './base-svg-prompt';

export function buildProcessesPrompt(data: BulkEAImport): ChatMessage[] {
    const orgs = summarizeEntities(data.organizations, [
        'division', 'department', 'team', 'key_responsibilities',
    ]);
    const bizSvcs = summarizeEntities(data.business_services, [
        'service_domain', 'business_service', 'service_offering', 'channel',
    ]);
    const rels = data.relationships
        .filter(r => r.relationship_type.toLowerCase().includes('process') ||
                     r.relationship_type.toLowerCase().includes('flow') ||
                     r.source_type === 'Organization' || r.target_type === 'BusinessService')
        .slice(0, 30);
    const relsSummary = summarizeEntities(rels, [
        'source_type', 'source_id', 'relationship_type', 'target_id',
    ]);

    return [
        {
            role: 'user',
            content: `${baseSvgRules()}

DIAGRAM TYPE: Business Process Swimlane Diagram

DATA - Organization Units:
${orgs}

DATA - Business Services:
${bizSvcs}

DATA - Process Relationships:
${relsSummary || '(derive logical process flow from org→service relationships)'}

INSTRUCTIONS:
- Create horizontal swimlane diagram with departments as lanes.
- Each lane has a colored header on the left with the department name.
- Place process steps as rounded rectangles within the appropriate lane.
- Process steps correspond to business services or key responsibilities.
- Draw arrows between steps to show the flow sequence.
- Use decision diamonds for branching points if applicable.
- Use blue fill for process steps, amber for decision points, green for start/end.
- Add a title "Business Process Flows" at the top.
- Show 3-5 main process flows derived from the data.`,
        },
    ];
}
